import React, { useState, useEffect, useCallback } from "react";
import { MANAGER_NAV_SECTIONS } from "./adminTabsConfig";
import { adminApiRequest } from "../utils/adminApi";

const Sidebar = ({
  activeTab,
  handleTabClick,
  isSidebarOpen,
  setIsSidebarOpen,
  onLogout,
}) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [counts, setCounts] = useState({});

  const fetchCounts = useCallback(async () => {
    try {
      const res = await adminApiRequest({ script: "get_order_counts" });
      if (res?.success) {
        setCounts(res.counts || {});
      }
    } catch (err) {
      console.error("Failed to fetch sidebar counts:", err);
    }
  }, []);

  useEffect(() => {
    fetchCounts();
    const interval = setInterval(fetchCounts, 30000); // Every 30 seconds
    return () => clearInterval(interval);
  }, [fetchCounts]);

  const navButtonClass = (isActive) =>
    `flex w-full items-center rounded-xl text-left text-sm font-bold transition-all duration-200 ${
      isCollapsed ? "justify-center px-3 py-3" : "gap-3 px-4 py-3"
    } ${
      isActive
        ? "bg-emerald-500/15 text-emerald-400 ring-1 ring-emerald-500/30"
        : "text-slate-400 hover:bg-slate-800 hover:text-white"
    }`;

  return (
    <>
      {isSidebarOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/40 lg:hidden"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      <aside
        className={`fixed inset-y-0 left-0 z-40 flex h-screen flex-col overflow-hidden bg-slate-900 text-white shadow-2xl transition-all duration-300 lg:static lg:translate-x-0 ${
          isCollapsed ? "w-24" : "w-64"
        } ${isSidebarOpen ? "translate-x-0" : "-translate-x-full"}`}
      >
        <div
          className={`shrink-0 border-b border-slate-800 ${
            isCollapsed ? "px-3 py-4" : "px-6 py-6"
          }`}
        >
          <div className="flex items-start justify-between gap-3">
            <div
              className={`flex min-w-0 items-center ${
                isCollapsed ? "justify-center w-full" : "gap-3"
              }`}
            >
              {!isCollapsed && (
                <div className="min-w-0">
                  <h1 className="truncate text-xl font-bold tracking-tight uppercase">
                    Kinews
                  </h1>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Admin Portal</p>
                </div>
              )}
            </div>

            {!isCollapsed && (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setIsCollapsed(true)}
                  className="hidden rounded-lg border border-slate-700 px-2 py-1 text-slate-400 transition hover:bg-slate-800 hover:text-white lg:block"
                >
                  <i className="fas fa-angle-left"></i>
                </button>

                <button
                  onClick={() => setIsSidebarOpen(false)}
                  className="text-slate-400 hover:text-white lg:hidden"
                >
                  ✕
                </button>
              </div>
            )}

            {isCollapsed && (
              <button
                onClick={() => setIsCollapsed(false)}
                className="absolute right-3 top-4 hidden rounded-lg border border-slate-700 px-2 py-1 text-slate-400 transition hover:bg-slate-800 hover:text-white lg:block"
              >
                <i className="fas fa-angle-right"></i>
              </button>
            )}
          </div>
        </div>

        <div className="relative min-h-0 flex-1 overflow-hidden">
          <div
            className={`sidebar-scrollbar h-full overflow-y-auto ${
              isCollapsed ? "px-2 py-4" : "px-4 py-6"
            }`}
          >
            <div className="space-y-8">
              {MANAGER_NAV_SECTIONS.map((section) => (
                <div key={section.title} className="space-y-2">
                  {!isCollapsed && (
                    <h3 className="px-4 text-[11px] font-bold uppercase tracking-[0.15em] text-slate-500/80">
                      {section.title}
                    </h3>
                  )}
                  <div className="space-y-1">
                    {section.items.map((item) => (
                      <button
                        key={item.key}
                        onClick={() => handleTabClick(item.key)}
                        className={navButtonClass(activeTab === item.key)}
                        title={isCollapsed ? item.label : ""}
                      >
                        <div className="relative flex items-center justify-center">
                          <span className="text-lg leading-none">{item.icon}</span>
                          {isCollapsed && counts[item.key] > 0 && (
                            <span className="absolute -right-2 -top-2 flex h-4 w-4 items-center justify-center rounded-full bg-pink-500 text-[9px] font-black text-white ring-2 ring-slate-900">
                              {counts[item.key]}
                            </span>
                          )}
                        </div>
                        {!isCollapsed && (
                          <div className="flex flex-1 items-center justify-between gap-2 min-w-0">
                            <span className="truncate">{item.label}</span>
                            {counts[item.key] > 0 && (
                              <span className="flex h-5 min-w-[20px] items-center justify-center rounded-full bg-pink-500 px-1.5 text-[10px] font-black text-white shadow-lg shadow-pink-500/20">
                                {counts[item.key]}
                              </span>
                            )}
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div
          className={`shrink-0 border-t border-slate-800 ${
            isCollapsed ? "px-2 py-4" : "px-4 py-4"
          }`}
        >
          <button
            onClick={onLogout}
            className={`flex w-full items-center justify-center rounded-xl border border-red-500/40 bg-red-500/10 text-sm font-medium text-red-400 transition-all duration-200 hover:bg-red-500/20 hover:text-red-300 ${
              isCollapsed ? "px-3 py-3" : "gap-2 px-4 py-3"
            }`}
          >
            <span>↩</span>
            {!isCollapsed && <span>Logout</span>}
          </button>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;